import { ExternalLinkIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Flex,
  FormLabel,
  Icon,
  Link,
  Text,
} from "@chakra-ui/react";
import React from "react";
import { RiEditLine } from "react-icons/ri";
import { Card } from "src/components/Layout/Card";
import { WithRequiredHasuraRoles } from "src/components/Permissions/WithRequiredHasuraRoles";
import { useEditFormTemplateContext } from "src/scenes/orgAdmin/formTemplates/ContentTab/V1/context";
import { useFormBuilderV1Feature } from "src/scenes/orgAdmin/formTemplates/hooks/useFormBuilderV1Feature";
import * as Draft from "src/scenes/orgAdmin/formTemplates/types/draft";
import * as AF from "src/types/formTemplate";
import { HasuraRole } from "src/types/hasuraRole";
import * as RD from "src/types/remoteData";
import { UnpublishedChangesAlert } from "../../UnpublishedChangesAlert";
import {
  hasInternalOnlyTag,
  InternalOnlyTag,
} from "../QuestionExtraInfo/InternalOnlyTag";
import {
  hasRequirementTag,
  RequirementTag,
} from "../QuestionExtraInfo/RequirementTag";
import {
  hasVerificationTags,
  VerificationTag,
} from "../QuestionExtraInfo/VerificationTag";
import { SchoolItem } from "../types";
import { AvailableAnswers } from "./AvailableAnswers";
import { QuestionTitle } from "./QuestionTitle";
import { QuestionType } from "./QuestionType";
import { Schools } from "./Schools";

type ViewProps = {
  question: Draft.Question;
  hasChanges: boolean;
  onEditButtonClick?: () => void;
  onRemoveButtonClick?: () => void;
  schools: RD.RemoteData<unknown, SchoolItem[]>;
};
export const QuestionFormView: React.FC<ViewProps> = ({
  question,
  hasChanges,
  onEditButtonClick,
  onRemoveButtonClick,
  schools,
}) => {
  const { isFormBuilderFullEdit } = useFormBuilderV1Feature();
  const { invalidQuestionVisibility } = useEditFormTemplateContext();
  const hasInvalidQuestionVisibilty = invalidQuestionVisibility.includes(
    question.id
  );

  return (
    <Card
      display="flex"
      flexDirection="column"
      showBorder
      gap="3"
      padding={6}
      id={question.key ?? question.id}
    >
      {hasChanges ? <UnpublishedChangesAlert isNew={question.isNew} /> : null}
      {hasInvalidQuestionVisibilty ? (
        <Alert rounded="md" status="warning">
          <AlertIcon />
          <AlertDescription>
            This question may no longer be conditionally revealed because its
            originating question was removed or changed, check its
            configurations
          </AlertDescription>
        </Alert>
      ) : null}
      <Flex direction="column" gap="3">
        <Tags question={question} />
        <Flex justifyContent="space-between" alignItems="flex-start">
          <QuestionTitle question={question} />

          <WithRequiredHasuraRoles
            roles={[HasuraRole.ADMIN, HasuraRole.ORG_ADMIN]}
          >
            {isFormBuilderFullEdit && (
              <Flex direction="row" gap="2">
                {question.isNew && onRemoveButtonClick && (
                  <Button
                    colorScheme="gray"
                    variant="outline"
                    onClick={onRemoveButtonClick}
                  >
                    Remove question
                  </Button>
                )}
                {onEditButtonClick && (
                  <Button
                    colorScheme="gray"
                    variant="outline"
                    leftIcon={<RiEditLine />}
                    onClick={onEditButtonClick}
                  >
                    Edit question
                  </Button>
                )}
              </Flex>
            )}
          </WithRequiredHasuraRoles>
        </Flex>
        <Flex direction="column" gap="4">
          <QuestionType questionType={question.type} />
          <AvailableAnswers
            question={Draft.fromOriginalQuestion(question)}
            schools={schools}
          />
        </Flex>
        <Flex direction="column" gap="1">
          <QuestionLink question={question} />
        </Flex>
        <Schools question={question} schools={schools} />
      </Flex>
    </Card>
  );
};

type QuestionLinkProps = { question: AF.Question<AF.WithId> };
const QuestionLink: React.FC<QuestionLinkProps> = ({ question }) =>
  question.link_text && question.link_url ? (
    <>
      <FormLabel as={Text} margin="0">
        Question link
      </FormLabel>
      <Link
        href={question.link_url}
        target="_blank"
        color="primary.500"
        alignItems="center"
        display="flex"
      >
        {question.link_text}
        <Icon marginLeft="5px" as={ExternalLinkIcon} />
      </Link>
      <Text fontSize="sm" color="gray.500">
        {question.link_url}
      </Text>
    </>
  ) : null;

type TagsProps = { question: AF.Question<AF.WithId> };
const Tags: React.FC<TagsProps> = ({ question }) => {
  if (!hasTags(question)) {
    return null;
  }

  return (
    <Flex direction="row" gap="3">
      <VerificationTag question={question} />
      <RequirementTag question={question} />
      <InternalOnlyTag question={question} />
    </Flex>
  );
};

function hasTags(question: AF.Question<AF.WithId>): boolean {
  return (
    hasVerificationTags(question) ||
    hasRequirementTag(question) ||
    hasInternalOnlyTag(question)
  );
}
